import { Trans, useLingui } from '@lingui/react/macro';
import { TournamentPlan } from 'modules/organizer/domain/interfaces/tournament-plan';
import { formatRublesFromMinorUnits } from 'modules/organizer/domain/utils/money';
import { formatSubscriptionPeriod } from 'modules/organizer/domain/utils/subscription-period';
import { buyPlanFormValidator, BuyPlanFormValues } from 'modules/organizer/domain/validators/wallet-topup.validator';
import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { theme } from 'styles/theme/theme';
import { SUBSCRIPTION_COPY, SUBSCRIPTION_FORM_CLASS } from './subscription-layout';
import { SubscriptionPlanPicker } from './subscription-topup-form';

interface SubscriptionBuyPlanFormProps {
  plans: TournamentPlan[];
  initialPlanId: number;
  balanceInMinorUnits: number;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (values: BuyPlanFormValues) => Promise<void>;
}

export const SubscriptionBuyPlanForm: React.FC<SubscriptionBuyPlanFormProps> = ({
  plans,
  initialPlanId,
  balanceInMinorUnits,
  isSubmitting,
  error,
  onSubmit,
}) => {
  const { t } = useLingui();

  const handleSubmit = async (values: BuyPlanFormValues, helpers: FormikHelpers<BuyPlanFormValues>) => {
    try {
      await onSubmit(values);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Formik<BuyPlanFormValues>
      initialValues={{ planId: initialPlanId }}
      enableReinitialize
      validationSchema={buyPlanFormValidator}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isSubmitting: formSubmitting }) => {
        const selected = plans.find((plan) => plan.id === values.planId);
        const canAfford = !!selected && balanceInMinorUnits >= selected.priceInMinorUnits;
        const shortfall = selected ? formatRublesFromMinorUnits(selected.priceInMinorUnits - balanceInMinorUnits) : '';

        return (
          <Form className={SUBSCRIPTION_FORM_CLASS}>
            <UDText type="headline">{t(SUBSCRIPTION_COPY.buyPlanTitle)}</UDText>
            <UDText type="body-text" color={theme.colors.Gray}>
              {t(SUBSCRIPTION_COPY.buyPlanHint)}
            </UDText>
            <SubscriptionPlanPicker
              plans={plans}
              selectedPlanId={values.planId}
              locked={false}
              onSelect={(planId) => setFieldValue('planId', planId)}
            />
            {selected && !canAfford && (
              <UDText type="footnote" color={theme.colors.CaseErrorText}>
                <Trans>Не хватает {shortfall} ₽. Пополните баланс.</Trans>
              </UDText>
            )}
            {error && (
              <UDText type="footnote" color={theme.colors.CaseErrorText}>
                {error}
              </UDText>
            )}
            <UDButton type="submit" variant="primary" loading={isSubmitting || formSubmitting} disabled={!values.planId || !canAfford}>
              <Trans>Оплатить с баланса</Trans>
              {selected ? ` · ${formatRublesFromMinorUnits(selected.priceInMinorUnits)} ₽` : ''}
            </UDButton>
          </Form>
        );
      }}
    </Formik>
  );
};
